import Phaser from 'phaser';

// リビジョン情報
export const REVISION = import.meta.env.GIT_REVISION || 'dev';

// 基本サイズの定義
const BASE_WIDTH = 450;
const BASE_HEIGHT = 800;

// 画面サイズに基づいてゲームサイズを計算
const calculateGameSize = () => {
    const parent = document.getElementById('game-container');
    const parentWidth = parent.clientWidth;
    const parentHeight = parent.clientHeight;
    
    let width = BASE_WIDTH;
    let height = BASE_HEIGHT;
    
    // アスペクト比を維持しながらサイズを調整
    const scale = Math.min(parentWidth / BASE_WIDTH, parentHeight / BASE_HEIGHT);
    width = Math.floor(BASE_WIDTH * scale);
    height = Math.floor(BASE_HEIGHT * scale);
    
    return { width, height, scale };
};

// ゲームの設定
export const config = {
    type: Phaser.AUTO,
    parent: 'game-container',
    scale: {
        mode: Phaser.Scale.FIT,
        parent: 'game-container',
        width: BASE_WIDTH,
        height: BASE_HEIGHT,
        autoCenter: Phaser.Scale.CENTER_BOTH,
        autoRound: true
    },
    physics: {
        default: 'matter',
        matter: {
            debug: false,
            gravity: { y: 2 }, // 重力は初期値は0に設定
        }
    },
    scene: [], // シーンは動的に追加されるため空の配列を設定
    input: {
        touch: {
            capture: true
        }
    }
};

// ゲームの定数
export const CONSTANTS = {
    EVALUATION: {
        // 評価の表示テキスト
        TEXT: {
            FAIL: 'F',
            PASS: 'C',
            GOOD: 'B',
            GREAT: 'A',
            PERFECT: 'S'
        },
        // 評価のテクスチャキー
        TEXTURE_KEYS: {
            FAIL: 'evaluation_fail',
            PASS: 'evaluation_pass',
            GOOD: 'evaluation_good',
            GREAT: 'evaluation_great',
            PERFECT: 'evaluation_perfect'
        },
        // テクスチャ生成時の設定
        SETTINGS: {
            FAIL: { color: '#ffffff' },    // 白
            PASS: { color: '#00ffff' },    // 水色
            GOOD: { color: '#00ff00' },    // 緑
            GREAT: { color: '#ff0000' },   // 赤
            PERFECT: { color: '#ff00ff' }  // 紫
        }
    },
    STRETCH_EVALUATION: {
        FAIL: { min: 0, max: 0.01, multiplier: 0, timeExtension: 0.2 },    // 0-1%
        PASS: { min: 0.01, max: 0.4, multiplier: 1, timeExtension: 0.3 },  // 1-40%
        GOOD: { min: 0.4, max: 0.7, multiplier: 2, timeExtension: 0.4 },   // 40-70%
        GREAT: { min: 0.7, max: 0.9, multiplier: 5, timeExtension: 0.5 },  // 70-90%
        PERFECT: { min: 0.9, max: Infinity, multiplier: 15, timeExtension: 1.0 } // 90%以上
    },
    RUBBER_BAND: {
        NATURAL_LENGTH: 65,      // ゴム紐の自然長（この長さまでは力がかからない）
        STIFFNESS: 0.01,        // ゴム紐の硬さ（小さいほど伸びやすい）
        DAMPING: 0.01,          // 減衰（大きいほど振動が早く収まる）
        MAX_STRETCH_FACTOR: 3.0   // 伸びの最大倍率
    },
    WEIGHT: {
        RADIUS: 10,          // 錘の半径
        MASS: 5,           // 錘の質量
        FRICTION: 0.05,      // 錘の摩擦係数
        RESTITUTION: 1.0,    // 錘の反発係数（ダイナミックな動きのため最大に）
        FRICTION_AIR: 0.05,  // 空気抵抗
    },
    TARGET: {
        RADIUS: 30,          // 的の固定半径
        POINTS: 1,          // 的の固定得点
        TYPES: {
            YELLOW: "yellow"  // 黄色のターゲット
        },
        TYPE_WEIGHTS: {      // 種類ごとの出現確率
            YELLOW: 1.0
        }
    },
    COMBO: {
        MAX_TIMEOUT_MS: 3000,     // コンボ猶予時間の最大値（3秒）
        EXTENSION_MS: 1000,       // 1回の延長時間（1秒）
    },
    TIMER: {
        INITIAL_TIME: 30,      // 初期時間（秒）
        DISPLAY_PRECISION: 0.1,  // 表示精度（秒）
        UPDATE_INTERVAL: 100,    // 更新間隔（ミリ秒）
    },
    MOVING_TARGET: {
        SPEED: 2,           // 基本移動速度
        START_COUNT: 50,    // 動き始めるターゲットの順番
        DIRECTIONS: [       // 移動方向（斜め4方向）
            { x: 1, y: 1 },   // 右下
            { x: -1, y: 1 },  // 左下
            { x: 1, y: -1 },  // 右上
            { x: -1, y: -1 }  // 左上
        ]
    },
    EFFECTS: {
        SPLASH: {
            SPEED: { min: 100, max: 400 },        // パーティクルの飛び散る速度範囲（重力の影響を受ける）
            SCALE: { start: 1.6, end: 0 },        // パーティクルの大きさ（小さめ）
            BASE_LIFESPAN: 1000,                  // エフェクトの基本持続時間（ミリ秒）
            BASE_QUANTITY: 40,                    // 1回の爆発で発生するパーティクルの基本数
            GRAVITY: 400,                         // パーティクルにかかる重力
            CIRCLE_RADIUS: 20,                    // パーティクルが発生する円の半径
            POINTS: {
                FAIL: {
                    scale: 1.8,                   // 「不可」でも派手に見えるよう1.4倍のサイズ
                    quantity: 1.4,                // パーティクル数も1.4倍
                    lifespan: 1.0                 // 基本の持続時間
                },
                PASS: {
                    scale: 2.0,
                    quantity: 1.6,
                    lifespan: 1.1
                },
                GOOD: {
                    scale: 2.4,
                    quantity: 1.8,
                    lifespan: 1.2
                },
                GREAT: {
                    scale: 2.8,
                    quantity: 2.0,
                    lifespan: 1.3
                },
                PERFECT: {
                    scale: 3.2,
                    quantity: 2.2,
                    lifespan: 1.4
                }
            },
            COLORS: {
                YELLOW: 0xffff00,
                RED: 0xff0000,
                GREEN: 0x00ff00
            }
        }
    }
};

// グローバル変数
export const gameState = {
    highScore: 0,
    bgmEnabled: true, // BGMの設定（デフォルトでオン）
    playerName: localStorage.getItem('playerName') || 'NONAME', // プレイヤー名を追加
    
    // プレイ毎の状態を管理するオブジェクト
    playState: {
        score: 0,
        gameOver: false,
        currentSeed: null, // ターゲット生成用のSeed値
        playId: null, // プレイID
        maxComboCount: 0, // 最大コンボ数
        playTime: 0, // プレイ時間（秒）
        evaluationCounts: {
            FAIL: 0,
            PASS: 0,
            GOOD: 0,
            GREAT: 0,
            PERFECT: 0
        }
    }
};
